import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK } from '@/game/config';
import { getBackground, setBackground, type Background } from '@/game/storage';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { SUPPORTED_LANGS, LANG_FLAG, type Lang } from '@/i18n';
import { lessonsForLang, getActivePack, setActivePack, packKey } from '@/game/packs';
import { trackLanguageChange, trackLessonChange } from '@/analytics';
import { switchLanguage } from '@/game/language';
import {
  UFO_MAX, DIAMOND_MAX, ICE_MAX, SAT_MAX, GALAXY_MAX, PASTA_MAX, CAT_MAX, WORM_MAX, ROCKET_MAX,
  isRocketReadLang,
} from '@/game/progression/config';
import { ReadingProgression, preRoundStats, type SessionSummary } from '@/game/progression/ReadingProgression';
import { lessonProgress } from '@/game/progression/lessonStats';
import { treatSlots } from '@/game/progression/treats';
import { TREAT_SLOTS } from '@/game/ui/menuSlots';
import { maybeSeedProgress } from '@/game/progression/devSeed';
import { ProgressBar } from '@/game/ui/ProgressBar';
import { Starfield } from '@/game/objects/Starfield';
import { openPrivacy } from '@/ui/privacyOverlay';
import { openBackgroundPicker } from '@/ui/backgroundPicker';
import { openLanguagePicker } from '@/ui/languagePicker';
import { maybeShowVoiceHint, checkVoiceHintOnce } from '@/ui/voiceHintOverlay';
import { openLessonPicker } from '@/ui/lessonPicker';
import { closeChoicePicker } from '@/ui/choicePicker';
import { openScoreboard, closeScoreboard, PLANET_PX } from '@/ui/scoreboard';
import { hasUnseenRecords } from '@/game/progression/scoreboard';
import { PlanetStack } from '@/game/ui/PlanetStack';
import { buildStamp, devFlag, devParam } from '@/game/dev';
import { applyPendingUpdate } from '@/platform/swUpdate';
import { isWeb, isWebPlayLocked, recordStars } from '@/game/promo';
import { isClipMode, isClipLessonSpent } from '@/game/clip';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { showInfoCard } from '@/game/ui/infoCard';
import { addEventTile } from '@/game/ui/eventTile';
import { activeEvent, consumeLaunchEvent, eventWelcomed, EVENT_POOL_ID } from '@/game/season';
import { starGlyphsFor } from '@/game/starGlyphs';
import { showClipOutro } from '@/game/ui/clipOutro';
import { showWebLimitCard } from '@/game/ui/webLimitCard';
import { StreakChip } from '@/game/ui/streakChip';
import {
  currentStreakView, maybeSeedStreak, streakEnabled, takeStreakEvent,
} from '@/game/progression/streakSession';
import { createStoreBadge } from '@/game/ui/storeBadges';
import type { Pack } from '@/data/packs/types';

interface MenuInit {
  summary?: SessionSummary | null;
  /** Set by RoundScene.backToMenu — this menu visit is the end of a game, not a fresh launch. */
  fromRound?: boolean;
  /**
   * App Clip only: come back with the lesson picker already open. Sent by the end-of-trial card's
   * "try another lesson" action (src/game/ui/clipOutro.ts) — the whole point of that action is the
   * picker, so making the player find the pill again would waste the one tap they gave us.
   */
  pickLesson?: boolean;
  /**
   * Web only: a game scene was opened after the free allowance ran out and bounced back here
   * (src/game/ui/webLimitCard.ts `redirectIfWebLimit`) — show the "continue in the app" card.
   */
  webLimit?: boolean;
}

/** App Store badge height in game units; the Play badge is drawn taller to match optically. */
const CTA_BADGE_HEIGHT = 38;

/**
 * WEB ONLY — the menu's call-to-action row: the App Store badge, the Play button, the Play Store
 * badge. The browser build is the full game for free, so without these it never tells anyone the
 * paid app exists (see src/game/promo.ts); beside the Play button is the one spot on the menu a
 * parent's eye is already on.
 *
 * All three go in as a SINGLE Phaser DOM element laid out by flexbox, rather than three elements
 * positioned from measured widths. That keeps the row symmetric whatever language the "Play"
 * label is in, and sidesteps a rounding quirk in Phaser's DOM layer that leaves an individually
 * positioned element a few pixels off its game-space centre — enough to make the two gaps around
 * the button visibly unequal.
 *
 * Phaser forces the element it positions to `display: block`, so the flex row lives on a child —
 * the same trick as `.menu-play__inner`.
 */
function webCtaRow(playBtn: HTMLElement): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'menu-cta';
  const row = document.createElement('span');
  row.className = 'menu-cta__row';
  row.append(
    createStoreBadge('appstore', CTA_BADGE_HEIGHT),
    playBtn,
    createStoreBadge('googleplay', CTA_BADGE_HEIGHT),
  );
  wrap.append(row);
  return wrap;
}

export class MenuScene extends Phaser.Scene {
  private ufos: Phaser.GameObjects.DOMElement[] = [];
  private diamonds: Phaser.GameObjects.DOMElement[] = [];
  private cubes: Phaser.GameObjects.DOMElement[] = [];
  private cats: Phaser.GameObjects.DOMElement[] = [];
  private worms: Phaser.GameObjects.DOMElement[] = [];
  private rockets: Phaser.GameObjects.DOMElement[] = [];
  private satellites: Phaser.GameObjects.DOMElement[] = [];
  private plates: Phaser.GameObjects.DOMElement[] = [];
  private galaxies: Phaser.GameObjects.DOMElement[] = [];

  constructor() {
    super({ key: 'Menu' });
  }

  create(data?: MenuInit): void {
    this.input.setDefaultCursor('default');
    this.ufos = [];   // fresh per create() — the previous DOM elements were torn down on restart
    this.diamonds = [];
    this.galaxies = [];

    // Consume the init data NOW: Phaser keeps settings.data until a start() passes new truthy
    // data, so a later plain restart()/start('Menu') would re-deliver this {summary} and replay
    // the old lesson's stats/celebration on the bar (e.g. right after switching lessons).
    this.sys.settings.data = {};

    // A settings dialog opened from the outgoing scene instance (e.g. during the font load that
    // precedes a language-switch restart) holds stale options — drop it with the old scene.
    closeChoicePicker();
    closeScoreboard();   // same reason: its numbers belong to the outgoing scene's lesson/language

    // Backing out of a round mid-game ends a game, and THAT exit — and only that one — opens the
    // record card on arrival (specs/scoreboard.md §5.2). The point is engagement rather than
    // bookkeeping: a child who quits on round 3 with one of three stars lit is being shown, in the
    // same breath, the two that are still there to win. A game played to its last heart has already
    // had its screen; that player gets the pulsing target instead (§5.5).
    //
    // Everything the menu would otherwise play out on its own waits for that card — a star raining
    // onto the journey bar behind an opaque panel is a star nobody sees. Without a card due,
    // `queue` runs its work immediately and the menu behaves exactly as it always did.
    const cardDue = Boolean(data?.fromRound);
    const afterCard: Array<() => void> = [];
    const queue = (run: () => void): void => { if (cardDue) afterCard.push(run); else run(); };

    // Dev/staging only: `?seed=<n>` fills in a part-finished journey so store captures don't show
    // a zero state. It runs HERE, ahead of every read below, so the URL describes the state before
    // anything decides on it — including the celebration backstop, which is how `?seed=999` (a
    // journey seeded to completion) reaches CelebrationScene in a single load rather than needing
    // a second one. Idempotent per lesson per page load.
    maybeSeedProgress();

    // Backstop for an owed end-of-journey celebration. Both in-round exits hand over directly, so
    // this only fires when neither ran — the app was killed (or crashed) between mastering the
    // last word and leaving the round. Checked before anything is built so we don't construct a
    // whole menu just to leave it, and via the cheap store read rather than a full engine.
    if (lessonProgress(getActivePack()).celebrationPending) {
      this.scene.start('Celebration', { score: 0 });
      return;
    }

    // The Menu is the safe moment to apply a background-downloaded update (no active round,
    // progress is persisted, so the single reload is seamless). Skipped while a "newly mastered"
    // celebration is about to play so the reload doesn't cut it off — it applies next menu visit.
    if (!(data?.summary && data.summary.newlyMastered.length > 0)) {
      applyPendingUpdate();
    }

    // Deep Field backdrop behind the whole menu. Shooting stars are enabled only here — during a
    // round a moving streak reads as a game object (see Starfield's StarfieldOptions).
    new Starfield(this, { shootingStars: true });

    this.addTitle();

    // Background-sound mute toggle (bottom-left). The same control sits in the same corner during
    // gameplay (Board / Survival). In the menu no ambience is playing, so a toggle here just
    // persists and takes effect on the next board start — no live start/stop needed.
    addMuteToggle(this);

    // "What is this game?" — the ℹ️ glyph, top-right (see addInfoButton).
    this.addInfoButton();

    // Day streak chip, left of the ℹ️ (specs/day-streak.md §5). A smash that qualified the day and
    // was not celebrated on a Summary (the child backed out of the round) is celebrated here.
    if (streakEnabled()) {
      maybeSeedStreak();   // dev/staging `?streak=N` only
      const chip = new StreakChip(this, currentStreakView());
      const ev = takeStreakEvent();
      if (ev) this.time.delayedCall(600, () => chip.playEvent(ev));
    }

    // Play (primary action) — a native HTML button (like the settings buttons) so touch is
    // reliable. The round play icon is drawn in CSS (`.menu-play__icon`).
    const playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'menu-play';
    // Inner flex wrapper: Phaser forces the positioned button to `display: block`, so the
    // icon/text layout (flex gap + vertical centering) must live on a child it doesn't touch.
    const playInner = document.createElement('span');
    playInner.className = 'menu-play__inner';
    const playIcon = document.createElement('span');
    playIcon.className = 'menu-play__icon';
    const playText = document.createElement('span');
    playText.textContent = i18next.t('play');
    playInner.append(playIcon, playText);
    playBtn.append(playInner);
    playBtn.addEventListener('click', () => this.startRound());
    // Web only, the store badges flank the Play button — so the whole row goes in as ONE DOM
    // element (see webCtaRow). The apps add the bare button exactly as before.
    this.add.dom(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 70, isWeb() ? webCtaRow(playBtn) : playBtn);

    // The web buy card. Due when this menu visit ends a game (Back out of a round — the Summary
    // and Celebration screens ask for themselves), and forced by `?buy` so it can be reviewed
    // without playing at all (dev/staging only; devFlag() is inert on production).
    //
    // When newly mastered words came back with us the journey bar below plays its falling-star
    // celebration first (it starts at 450ms and runs ~2s) — the card waits it out rather than
    // landing on top of it.
    if (data?.webLimit) {
      queue(() => showWebLimitCard(this));
    } else if (data?.fromRound || devFlag('buy')) {
      queue(() => maybeShowBuyPrompt(this, { delayMs: data?.summary ? 2600 : 1200 }));
    }

    // `?outro` shows the end-of-trial card here and now (dev/staging only). Reaching it for real
    // costs five boards of a lesson, which is a long way to go to look at a card — and the card
    // rotates, so checking all four cost twenty. Reload to see the next one; `?outro=md` renders
    // it one size down, which is how the shipped size was chosen.
    if (devFlag('outro')) {
      queue(() => showClipOutro(this, {
        delayMs: 0,
        size: devParam('outro') === 'md' ? 'md' : undefined,
        onPickLesson: () => this.scene.restart({ pickLesson: true }),
      }));
    }

    // Journey progress bar (top) + reset for the ACTIVE lesson. Every lesson has its own
    // progress now, so the bar always reflects the currently selected lesson (and re-renders
    // when the lesson picker below switches it — see the Lesson cycler's onCycle).
    {
      // Seeded once at the top of create() already; this call is for the ACTIVE lesson, which the
      // picker may have switched since (the seed is a no-op for a lesson already done this page
      // load). Must run before the engine below snapshots the stored blob.
      maybeSeedProgress();
      const activeLesson = getActivePack();
      const progression = new ReadingProgression(activeLesson.lang, activeLesson);
      const bar = new ProgressBar(this, GAME_WIDTH / 2, GAME_HEIGHT - 86, {
        width: 1040,
        rewardIcon: activeLesson.reward?.icon,
        rewardLabelKey: activeLesson.reward?.labelKey,
        glyphs: starGlyphsFor(activeLesson),
        // The reward target opens this lesson's record card (specs/scoreboard.md). Wired here and
        // not on the Summary's bar, which is mid-animation and sits under a tap-anywhere catcher.
        // The card is HTML in the overlay layer and has no scene of its own, so the planet it
        // shows is drawn HERE, by the same code that draws the in-game column.
        onTargetClick: () => {
          bar.stopTargetPulse();   // invitation accepted (see the pulse below)
          openScoreboard(getActivePack(), (name) => PlanetStack.portrait(this, name, PLANET_PX));
        },
      });
      // The one cue that the target is a button, and it only appears when there is something new
      // behind it: records that have moved since the card was last opened (specs/scoreboard.md
      // §5.5). Skipped when the card is about to open by itself — it would mark them read half a
      // second later, and a pulse that answers itself is noise.
      if (!cardDue && hasUnseenRecords(activeLesson)) bar.pulseTarget();

      // After a mid-round back-out that mastered new words, replay the same falling-star
      // celebration here; otherwise just show the current journey state.
      const summary = data?.summary ?? null;
      if (summary && summary.newlyMastered.length > 0) {
        const start = preRoundStats(summary);
        bar.setStats(start, false);
        queue(() => this.time.delayedCall(450, () => bar.animateGains(start, summary.stats)));
        recordStars(summary.stats.stars, packKey(activeLesson));
      } else {
        const stats = progression.stats();
        bar.setStats(stats);
        recordStars(stats.stars, packKey(activeLesson));
      }

      // Deliberately understated reset, centred under the journey bar, behind a two-step
      // confirmation whose second step is a multiplication (see showResetChallenge).
      //
      // It used to sit in the bottom-RIGHT corner, which is the worst place for it on a phone: it
      // is exactly where a thumb rests, and one stray tap away from the most destructive action in
      // the game. Centred, it is deliberate to reach and reads as part of the journey bar it
      // belongs to.
      const reset = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 14, i18next.t('menu.reset'), {
        fontFamily: FONT_STACK, fontSize: '14px', color: '#5a6470',
      }).setOrigin(0.5, 1).setInteractive({ useHandCursor: true });
      reset.on('pointerover', () => reset.setColor('#aabbcc'));
      reset.on('pointerout', () => reset.setColor('#5a6470'));
      reset.on('pointerdown', () => this.showResetConfirm(() => {
        progression.reset();
        bar.setStats(progression.stats());
        this.ufos.forEach((u) => u.destroy());
        this.ufos = [];
        this.diamonds.forEach((d) => d.destroy());
        this.diamonds = [];
        this.cubes.forEach((c) => c.destroy());
        this.cubes = [];
        this.satellites.forEach((sat) => sat.destroy());
        this.satellites = [];
        this.galaxies.forEach((g) => g.destroy());
        this.galaxies = [];
        this.plates.forEach((p) => p.destroy());
        this.plates = [];
        this.cats.forEach((c) => c.destroy());
        this.cats = [];
        this.worms.forEach((w) => w.destroy());
        this.worms = [];
        this.rockets.forEach((r) => r.destroy());
        this.rockets = [];
      }));

      // UFO "treat": earned every 3 stars OR by reaching Mars in one game, spent on a Survival
      // run. Shown upper-left when banked.
      this.addUfo(progression, activeLesson.lang);
      // 💎 Diamond "treat": earned by reaching Saturn in one game, spent on a Pairs run. Sits in
      // the gaps between the UFOs (specs/pairs-mode.md §3.2).
      this.addDiamond(progression, activeLesson.lang);
      // 🧊 Ice Cube "treat": earned by reaching Neptune in one game, spent on an Ice Maze run.
      // Sits OUTSIDE the saucers, in the menu's last free column (specs/word-maze-mode.md §3.2).
      this.addCube(progression, activeLesson.lang);
      // 🛰️ Satellite "treat": spent on a Word Labyrinth run. R3 of the outer column, facing the
      // Wagon's Galaxy across the bottom of the menu (specs/labyrinth-mode.md §3.2).
      this.addSatellite(progression, activeLesson.lang);
      // 🌌 Galaxy "treat": earned at the top of the whole planet ladder, spent on a Word Wagon run.
      // Exactly one, in the last free slot on the menu (specs/wagon-mode.md §3.2).
      this.addGalaxy(progression, activeLesson.lang);
      // 🍝 Plate "treat": earned at the eclipse, spent on a Recipe Kitchen run. Up to two, in the
      // outer column's upper gaps (specs/recipe-mode.md §3.2).
      this.addPlate(progression, activeLesson.lang);
      // 🐱 Cat "treat": earned at Venus — star 2, the lowest rung there is — and spent on a Cat &
      // Mouse letter hunt. The seventh treat kind, in the slot addPlate reserved for one
      // (specs/cat-mouse-mode.md §3.2).
      this.addCat(progression, activeLesson.lang);
      // 🪱 Space Worm "treat": earned at star 13, where the Sun reddens — the first prize above the
      // full stack — and spent on a Space Worm run. The eighth treat kind, two of them in the inner
      // column's top row (specs/space-worm-mode.md §3.2).
      this.addWorm(progression, activeLesson.lang);
      // 🚀 Rocket "treat": earned at star 15, where the Sun turns violet, and spent on a Rocket Read
      // run. The ninth treat kind, in the inner column's second row (specs/rocket-read-mode.md §3.2).
      this.addRocket(progression, activeLesson.lang);
    }

    // Settings pills (Background / Language / Lesson) as native HTML buttons in Phaser's DOM
    // layer. Canvas hit-testing was unreliable on touch (taps over the label text were dropped),
    // so these use real <button>s that get native, 100%-reliable touch + click handling.
    // Each pill opens a visual card dialog (src/ui/choicePicker.ts).
    const cx = GAME_WIDTH / 2;

    // Background picker — the pill opens a thumbnail dialog (one card per backdrop) instead of
    // blind-cycling on tap, so the player sees each option before choosing.
    let currentBg: Background = getBackground();
    const bgLabel = (b: Background) =>
      `🎭 ${i18next.t('menu.bgPrefix')}: ${i18next.t(`menu.bg.${b}`)}`;
    const bgBtn = document.createElement('button');
    bgBtn.type = 'button';
    bgBtn.className = 'menu-pill';
    bgBtn.textContent = bgLabel(currentBg);
    bgBtn.addEventListener('click', () => openBackgroundPicker(currentBg, (bg) => {
      currentBg = bg;
      setBackground(bg);
      bgBtn.textContent = bgLabel(bg);
    }));
    // The three settings pills are spaced 75px apart (up from 65) to keep a comfortable gap now
    // that each pill is ~20% taller (see .menu-pill padding in index.html) for easier tapping.
    this.add.dom(cx, GAME_HEIGHT / 2 + 20, bgBtn);
    // Background-sound on/off lives in the speaker glyph (top-right), not here.

    // Language picker — 🌐 prefix + country flag are language cues baked into the label.
    // The pill opens a flag + native-name card dialog (like the background picker above).
    const normalizeLang = (l: string): Lang =>
      (SUPPORTED_LANGS as readonly string[]).includes(l) ? (l as Lang) : 'de';
    const currentLang: Lang = normalizeLang(i18next.language);
    const langBtn = document.createElement('button');
    langBtn.type = 'button';
    langBtn.className = 'menu-pill';
    langBtn.textContent =
      `🌐 ${i18next.t('menu.langPrefix')}: ${i18next.t(`menu.lang.${currentLang}`)} ${LANG_FLAG[currentLang]}`;
    langBtn.addEventListener('click', () => openLanguagePicker(currentLang, async (lang) => {
      if (lang === currentLang) return;
      trackLanguageChange(lang);   // web demo only; after the no-op guard, so it counts real switches
      // i18next + script font + "re-resolve the lesson for the new language" — see language.ts.
      await switchLanguage(lang);
      // If this device has no voice for the language just picked, the readout will be silent from
      // here on — say so once, now, while the player is still thinking about the language.
      // (Kept here rather than inside switchLanguage: that module is node-tested and must stay free
      // of DOM/platform imports.) Renders in the new language, which i18next has already applied.
      maybeShowVoiceHint(lang);
      this.scene.restart({});         // re-renders everything (pill labels included) translated
    }));
    this.add.dom(cx, GAME_HEIGHT / 2 + 95, langBtn);

    // Lesson picker — only lessons for the CURRENT language (a lesson is always language-scoped).
    // First entry is the Daily Life journey; the rest are that language's arcade packs. The pill
    // opens a card dialog showing each lesson's journey symbol (ABC/Unicorn/themed star, planet).
    const lessons = lessonsForLang(currentLang);
    let currentPack: Pack = getActivePack();
    if (!lessons.some((p) => packKey(p) === packKey(currentPack))) currentPack = lessons[0] ?? currentPack;
    const packBtn = document.createElement('button');
    packBtn.type = 'button';
    // Single-lesson languages render a flat, non-tappable pill.
    packBtn.className = lessons.length > 1 ? 'menu-pill' : 'menu-pill menu-pill--disabled';
    packBtn.textContent = `📖 ${i18next.t('menu.packPrefix')}: ${currentPack.title}`;
    const pickLesson = (): void => openLessonPicker(lessons, currentPack, (pack) => {
      if (packKey(pack) === packKey(currentPack)) return;
      trackLessonChange(packKey(pack));   // web demo only
      setActivePack(pack);
      this.scene.restart({});   // re-render the journey bar + UFO for the newly selected lesson
    });
    packBtn.addEventListener('click', pickLesson);
    this.add.dom(cx, GAME_HEIGHT / 2 + 170, packBtn);

    // App Clip: arrived here from the end-of-trial card's "try another lesson". Open the picker
    // for them rather than making them hunt for the pill again.
    if (data?.pickLesson) this.time.delayedCall(120, pickLesson);

    // Seasonal event (Halloween): a pill at the top and, once, a welcome card. Arriving through the
    // store event's link (`?event=halloween`) shows the card even if it was seen before. Never in
    // the App Clip trial, and never straight after a game — that exit already owns the screen.
    const eventId = activeEvent(currentLang);
    const eventLesson = eventId ? lessons.find((p) => p.id === EVENT_POOL_ID[eventId]) : undefined;
    const viaEventLink = consumeLaunchEvent();
    if (eventId && eventLesson && !isClipMode()) {
      const alreadyActive = packKey(currentPack) === packKey(eventLesson);
      addEventTile(this, eventId, {
        alreadyActive,
        welcome: !cardDue && !data?.pickLesson && !alreadyActive
          && (viaEventLink === eventId || !eventWelcomed(eventId)),
        onStart: () => {
          trackLessonChange(packKey(eventLesson));   // web demo only
          setActivePack(eventLesson);
          this.scene.restart({});
        },
      });
    }

    // Privacy policy link (bottom-left). Required in-app for the Families/children
    // track. Opens the policy in an in-app overlay (not a new tab) so phone users
    // can always get back via the ✕ button or the device Back gesture.
    // Offset right of the bottom-left mute glyph so the two don't overlap.
    const privacy = this.add.text(60, GAME_HEIGHT - 14, i18next.t('menu.privacy'), {
      fontFamily: FONT_STACK, fontSize: '14px', color: '#5a6470',
    }).setOrigin(0, 1).setInteractive({ useHandCursor: true });
    privacy.on('pointerover', () => privacy.setColor('#aabbcc'));
    privacy.on('pointerout', () => privacy.setColor('#5a6470'));
    privacy.on('pointerdown', () => openPrivacy());

    this.addBuildStamp();

    // The card for a game left via "← Menü" (see `cardDue` at the top of create). Opened last, but
    // deliberately BEFORE checkVoiceHintOnce: the two share the single #overlay element, and the
    // voice hint's own guard skips itself when something is already showing there — which is the
    // behaviour we want, since it retries on the next menu visit and the card does not.
    if (cardDue) {
      openScoreboard(getActivePack(), (name) => PlanetStack.portrait(this, name, PLANET_PX), {
        onClose: () => { for (const run of afterCard) run(); },
      });
    }

    // Once per app session: if the language carried over from a previous session has no voice on
    // this device, the readout is silent — tell the player why. Last in create() so it can see
    // whether any other overlay (buy prompt, clip outro) already claimed the screen.
    checkVoiceHintOnce(currentLang);
  }

  /**
   * Info glyph (top-right) — opens the card that explains the game: smash the right words, collect
   * stars, reach the next planet (src/game/ui/infoCard.ts, read aloud on open).
   *
   * Understated on purpose, and styled like the mute glyph in the opposite corner: the menu's one
   * loud control is Play. Top-right is the last corner free of a control — the mute glyph and the
   * privacy link own the bottom-left, the reset link the bottom-right, and the UFO/diamond slots
   * start at y 150, well below this.
   *
   * The padded hit area is the same trick as addMuteToggle: the glyph itself is a small tap target
   * in a corner, so the rectangle is grown on all sides in the text's local space.
   */
  private addInfoButton(): void {
    const t = this.add.text(GAME_WIDTH - 18, 12, 'ℹ️', {
      fontFamily: FONT_STACK, fontSize: '28px',
    }).setOrigin(1, 0).setAlpha(0.7);
    const HIT_PAD = 18;
    t.setInteractive(
      new Phaser.Geom.Rectangle(-HIT_PAD, -HIT_PAD, t.width + HIT_PAD * 2, t.height + HIT_PAD * 2),
      Phaser.Geom.Rectangle.Contains,
    );
    t.input!.cursor = 'pointer';
    t.on('pointerover', () => t.setAlpha(1));
    t.on('pointerout', () => t.setAlpha(0.7));
    t.on('pointerdown', () => showInfoCard(this));
  }

  /**
   * Dev/staging only: which web build is on screen, top-left. Nothing renders on production —
   * buildStamp() returns null there.
   *
   * `?nostamp` suppresses it anyway, for the store-screenshot renderers: those capture a LOCAL
   * dev server on purpose (scripts/render-screenshots.mjs explains why — a production capture
   * silently ships whatever the last hand-deploy left behind), so without this the stamp would be
   * baked into every menu frame on both listings.
   *
   * The corner it sits in is the last free one: the mute glyph and the privacy link own the
   * bottom-left, the reset link the bottom-right, and the UFO slots start at x 220 / y 150.
   * Deliberately inert (no hit area, no hover) — it is a label, not a control.
   */
  private addBuildStamp(): void {
    if (devFlag('nostamp')) return;
    const stamp = buildStamp();
    if (!stamp) return;
    this.add.text(18, 12, stamp, {
      fontFamily: FONT_STACK, fontSize: '14px', color: '#5a6470',
    }).setOrigin(0, 0).setAlpha(0.75);
  }

  /**
   * The Play button. In the App Clip a lesson that has already spent its boards leads to the
   * end-of-trial card instead of another round — starting a round that ends on its own first
   * board would just look broken. Every other build goes straight through.
   */
  private startRound(): void {
    // The free web build is used up: answer the tap with the card here rather than hopping into
    // the Round scene only to be bounced straight back (which would also work — see webLimitCard).
    if (isWebPlayLocked(packKey(getActivePack()))) {
      showWebLimitCard(this);
      return;
    }
    if (isClipMode() && isClipLessonSpent(packKey(getActivePack()))) {
      showClipOutro(this, {
        delayMs: 0,   // this IS the response to their tap; a delay would read as a dead button
        onPickLesson: () => this.scene.restart({ pickLesson: true }),
      });
      return;
    }
    this.scene.start('Round');
  }

  /**
   * Title logo: the "ABC Star" mascot (the reading-journey reward art) standing in for "ABC",
   * next to a bold "Smash" with a cool light→deep blue vertical gradient, a navy stroke, and a
   * soft blue glow so it reads as a logo against the starfield. Built as a centered container so
   * a light entrance "pop" (scale-up with a soft bounce) can animate the whole group — safe here
   * since Phaser owns canvas transforms. Note: the pop tweens scale only (alpha stays 1) so the
   * title is always visible even if the tween is interrupted (a plain fade could strand it hidden).
   * The brand string is "ABC Smash" for every language (see i18n `title`); if it ever stops
   * starting with "ABC", the whole string renders as the gradient wordmark with no star.
   */
  private addTitle(): void {
    const raw = i18next.t('title');
    const hasABC = raw.startsWith('ABC');
    const rest = (hasABC ? raw.slice(3) : raw).trim().toLowerCase();   // "smash"

    // The "ABC" is the ABC-Star mascot texture (loaded in Preload), sized to sit beside the word.
    const parts: Phaser.GameObjects.GameObject[] = [];
    let starW = 0;
    if (hasABC) {
      const star = this.add.image(0, 0, 'abc_star').setOrigin(0.5);
      star.setScale(132 / star.height);        // ~132px tall, a touch larger than the word
      starW = star.displayWidth;
      parts.push(star);
    }

    const smash = this.add.text(0, 0, rest, {
      fontFamily: FONT_STACK, fontSize: '72px', fontStyle: 'bold',
      color: '#ffffff', stroke: '#0b1e3a', strokeThickness: 8,
    }).setOrigin(0, 0.5);
    // Cool light→deep blue vertical gradient on "Smash", matching the Play button.
    const grad = smash.context.createLinearGradient(0, 0, 0, smash.height);
    grad.addColorStop(0, '#e8f4ff');
    grad.addColorStop(0.55, '#7ec4ff');
    grad.addColorStop(1, '#3a7bd5');
    smash.setFill(grad);
    smash.setShadow(0, 0, '#4aa8ff', 14, true, true);
    parts.push(smash);

    // Lay out star + gap + word, then center the whole group horizontally.
    const gap = starW ? 12 : 0;
    const totalW = starW + gap + smash.width;
    if (starW) (parts[0] as Phaser.GameObjects.Image).x = -totalW / 2 + starW / 2;
    smash.x = -totalW / 2 + starW + gap;

    const container = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 205, parts);
    // Light entrance pop: scale up from 0.85 with a soft bounce (alpha stays 1).
    container.setScale(0.85);
    this.tweens.add({ targets: container, scale: 1, ease: 'Back.easeOut', duration: 500 });
  }

  /**
   * UFO "treats" — one per banked Survival run (earned every 3 stars or by reaching Mars in a
   * single game, capped at 6 by availableUfos). Rendered as up to three saucers down each side of the menu, filled in a
   * balanced left/right order. Each is a native DOM button (reliable touch, like the other menu
   * controls); tapping one spends a UFO and launches a Survival run. They bob gently via a Phaser
   * tween (Phaser owns the element's transform, so we must NOT animate transform in CSS).
   */
  private addUfo(progression: ReadingProgression, lang: string): void {
    // Dev/staging only: `?survival` forces a UFO to appear (no stars needed) and makes it
    // replayable — it doesn't spend from the bank. See src/game/dev.ts.
    // ...and a finished lesson shows one of these free forever (specs §6.5). Both cases, plus
    // `?treats`, come back as `forced` — the tap launches the run without claiming.
    const journeyDone = progression.journeyDone();
    const { count, free: forced } = treatSlots({
      banked: progression.availableUfos(),
      cap: UFO_MAX,
      journeyDone,
      showAll: devFlag('treats'),
      forceOne: devFlag('survival'),
    });
    if (count <= 0) return;

    // Three slots per side are laid out, but UFO_MAX is 4, so only L1, R1, L2, R2 are ever used
    // — the third row (y 480) is free and the Galaxy/Satellite columns sit outboard of it. Filled
    // L1, R1, L2, R2 so 1→2 UFOs stay balanced.
    // Inset sits ~halfway between the screen edge and the centred menu-button column (whose widest
    // pill spans roughly x≈440–840), so the saucers don't crowd the border.
    const slots = TREAT_SLOTS.ufo;

    // A finished lesson draws exactly ONE of every treat, and five of the six would take a LEFT
    // slot — five icons stacked down that edge with a lone Satellite opposite. The UFO and the
    // Diamond take their RIGHT slot in that one case, so the six sit three a side: the outer left
    // column keeps the Cube, the Plate and the Galaxy, and these two join the Satellite opposite.
    // Only when there is exactly one: a real bank (and `?treats`) still fills L, R, L, R.
    const mirrored = journeyDone && count === 1 ? 1 : 0;

    for (let i = 0; i < count && mirrored + i < slots.length; i++) {
      const { x, y } = slots[mirrored + i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-ufo';
      btn.setAttribute('aria-label', i18next.t('survival.play'));
      const alien = document.createElement('span');
      alien.className = 'menu-ufo__alien';
      alien.textContent = '👽';
      const saucer = document.createElement('span');
      saucer.className = 'menu-ufo__saucer';
      btn.append(alien, saucer);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimUfo();
        this.scene.start('Survival', { lang });
      });

      const dom = this.add.dom(x, y, btn);
      this.ufos.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 16, duration: 1500, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut', delay: i * 180,   // stagger so they don't bob in lockstep
      });
    }
  }

  /**
   * 💎 Diamond "treats" — one per banked Pairs run (earned by reaching Saturn in a single game,
   * capped at DIAMOND_MAX by availableDiamonds). Specs/pairs-mode.md §3.2.
   *
   * They sit in the **gaps between the UFOs**, which do not move: vertically at the midpoints of
   * the saucer rows, and inset toward the centre because that is the only geometry that fits — a
   * `.menu-ufo` button is 130×96, so the saucers occupy y 102–198 / 267–363 / 432–528 and the free
   * bands between them are 69px tall, too short for a second button at the same x. At x 345/935 a
   * 96×76 diamond spans x 297–393: clear of the UFO column (ends at 285) and of the centred pill
   * stack (starts around 440). Four of them then read as a diamond formation, which is a happy
   * accident rather than the reason.
   */
  private addDiamond(progression: ReadingProgression, lang: string): void {
    // Dev/staging only: `?pairs` forces a Diamond to appear (no stars needed) and makes it
    // replayable — it doesn't spend from the bank. `?pairs=3` also starts the run on round 3, so
    // the bigger boards can be seen without clearing the small ones. See src/game/dev.ts.
    // ...and a finished lesson shows one of these free forever (specs §6.5). Both cases, plus
    // `?treats`, come back as `forced` — the tap launches the run without claiming.
    const journeyDone = progression.journeyDone();
    const { count, free: forced } = treatSlots({
      banked: progression.availableDiamonds(),
      cap: DIAMOND_MAX,
      journeyDone,
      showAll: devFlag('treats'),
      forceOne: devFlag('pairs'),
    });
    if (count <= 0) return;

    const startRound = Number(devParam('pairs') ?? '') || 1;

    // Midpoints of the UFO rows, inset from the saucer column. Filled L1, R1, L2, R2 so 1→2
    // diamonds stay balanced, exactly like the UFOs.
    const slots = TREAT_SLOTS.diamond;

    // Mirrored for a finished lesson, exactly as addUfo above.
    const mirrored = journeyDone && count === 1 ? 1 : 0;

    for (let i = 0; i < count && mirrored + i < slots.length; i++) {
      const { x, y } = slots[mirrored + i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-diamond';
      btn.setAttribute('aria-label', i18next.t('pairs.play'));
      const gem = document.createElement('span');
      gem.className = 'menu-diamond__gem';
      gem.textContent = '💎';
      const plate = document.createElement('span');
      plate.className = 'menu-diamond__glow';
      btn.append(gem, plate);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimDiamond();
        this.scene.start('Pairs', { lang, startRound });
      });

      const dom = this.add.dom(x, y, btn);
      this.diamonds.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 12, duration: 1700, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut', delay: 240 + i * 200,   // offset from the saucers' bob, not in step
      });
    }
  }

  /**
   * 🧊 Ice Cube "treats" — one per banked Ice Maze run (earned by reaching Neptune in a single
   * game, capped at ICE_MAX by availableCubes). specs/word-maze-mode.md §3.2.
   *
   * They sit OUTWARD of the saucers rather than inward, because inward is full: the Diamonds
   * already took the gaps between the UFO rows at x 345/935, and the centred pill stack starts
   * around x 440. A `.menu-cube` button is 96×76, so at x 90 it spans x 42–138 — clear of the UFO
   * column (which starts at 155) and clear of the canvas edge, which Phaser.Scale.FIT letterboxes
   * away from any phone notch.
   *
   * The menu's outermost column. With ICE_MAX = 4 plus the Galaxy at L3 and the Satellite at R3
   * that is 16 buttons; the interleaved rows (y 233/398, where addDiamond puts the same box) are
   * still empty at this x, so the real ceiling is 20 — see specs/labyrinth-mode.md §3.2.
   */
  private addCube(progression: ReadingProgression, lang: string): void {
    // Dev/staging only: `?maze` forces an Ice Cube to appear (no stars needed) and makes it
    // replayable — it doesn't spend from the bank. `?maze=4` also starts the run on round 4, where
    // the diagonals live. See src/game/dev.ts.
    // ...and a finished lesson shows one of these free forever (specs §6.5). Both cases, plus
    // `?treats`, come back as `forced` — the tap launches the run without claiming.
    const { count, free: forced } = treatSlots({
      banked: progression.availableCubes(),
      cap: ICE_MAX,
      journeyDone: progression.journeyDone(),
      showAll: devFlag('treats'),
      forceOne: devFlag('maze'),
    });
    if (count <= 0) return;

    const startRound = Number(devParam('maze') ?? '') || 1;

    // Filled L1, R1, L2 — the same balanced order the UFOs and Diamonds use, so one cube sits
    // top-left and two sit across the top.
    const slots = TREAT_SLOTS.cube;

    for (let i = 0; i < count && i < slots.length; i++) {
      const { x, y } = slots[i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-cube';
      btn.setAttribute('aria-label', i18next.t('maze.play'));
      const cube = document.createElement('span');
      cube.className = 'menu-cube__ice';
      cube.textContent = '🧊';
      const plate = document.createElement('span');
      plate.className = 'menu-cube__glow';
      btn.append(cube, plate);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimCube();
        this.scene.start('Maze', { lang, startRound });
      });

      const dom = this.add.dom(x, y, btn);
      this.cubes.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 14, duration: 1900, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut', delay: 420 + i * 220,   // offset from both the saucers and the gems
      });
    }
  }

  /**
   * 🐱 Cat "treats" — one per banked Cat & Mouse run (earned at Venus, star 2, capped at CAT_MAX by
   * availableCats). specs/cat-mouse-mode.md §3.2.
   *
   * The positions come from `ui/menuSlots.ts`, which is the whole layout in one table with a test
   * that no two treats share a spot. That table exists because of this button: it first shipped into
   * `(1190, 398)`, `(90, 398)` and `(345, 480)` — the Satellite's second slot, the Galaxy's second
   * and the Plate's third — under a comment in this file asserting all three were free. They were
   * not, and the overlap only shows with several banks full at once, a state you have to earn.
   *
   * It now interleaves the SAUCER column the way the diamonds interleave the inner one: the gaps at
   * x 220 / 1060, which the UFOs (rows 1–3) and the Galaxy and Satellite (row 3) leave empty.
   */
  private addCat(progression: ReadingProgression, lang: string): void {
    // Dev/staging only: `?cat` forces a Cat to appear (no stars needed) and makes it replayable —
    // it doesn't spend from the bank. `?cat=3` also starts the run on board 3, where the second
    // door appears. See src/game/dev.ts.
    // ...and a finished lesson shows one of these free forever (specs §6.5). Both cases, plus
    // `?treats`, come back as `forced` — the tap launches the run without claiming.
    const { count, free: forced } = treatSlots({
      banked: progression.availableCats(),
      cap: CAT_MAX,
      journeyDone: progression.journeyDone(),
      showAll: devFlag('treats'),
      forceOne: devFlag('cat'),
    });
    if (count <= 0) return;

    const startBoard = Number(devParam('cat') ?? '') || 1;

    const slots = TREAT_SLOTS.cat;

    for (let i = 0; i < count && i < slots.length; i++) {
      const { x, y } = slots[i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-cat';
      btn.setAttribute('aria-label', i18next.t('cat.play'));
      const face = document.createElement('span');
      face.className = 'menu-cat__face';
      face.textContent = '🐱';
      const plate = document.createElement('span');
      plate.className = 'menu-cat__glow';
      btn.append(face, plate);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimCat();
        this.scene.start('Cat', { lang, startBoard });
      });

      const dom = this.add.dom(x, y, btn);
      this.cats.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 14, duration: 1900, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut', delay: 1100 + i * 200,   // offset from every other treat's bob
      });
    }
  }

  /**
   * 🪱 Space Worm "treats" — one per banked Space Worm run (earned at star 13, the red Sun, capped
   * at WORM_MAX by availableWorms). specs/space-worm-mode.md §3.2.
   *
   * Positions come from `ui/menuSlots.ts` like every other treat — the inner column's top row,
   * `(345, 150)` and `(935, 150)`, the highest pair on the menu for the highest rung on the ladder.
   * There are at most **two**: 163 smashes is a long run, and capping at two leaves the inner
   * column's second row as the menu's last symmetric pair (§3.2).
   *
   * The only treat whose face is an <img> rather than an emoji (§3.3), with 🪱 as its alt so a
   * missing file degrades to a glyph rather than to a broken-image box.
   */
  private addWorm(progression: ReadingProgression, lang: string): void {
    // Dev/staging only: `?worm` forces a Worm to appear (no stars needed) and makes it replayable —
    // it doesn't spend from the bank. `?worm=4` also starts the run at rung 4, where the picture
    // stops showing its word. See src/game/dev.ts.
    // ...and a finished lesson shows one of these free forever. Both cases, plus `?treats`, come
    // back as `forced` — the tap launches the run without claiming.
    const { count, free: forced } = treatSlots({
      banked: progression.availableWorms(),
      cap: WORM_MAX,
      journeyDone: progression.journeyDone(),
      showAll: devFlag('treats'),
      forceOne: devFlag('worm'),
    });
    if (count <= 0) return;

    const startRung = Number(devParam('worm') ?? '') || 1;

    const slots = TREAT_SLOTS.worm;

    for (let i = 0; i < count && i < slots.length; i++) {
      const { x, y } = slots[i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-worm';
      btn.setAttribute('aria-label', i18next.t('worm.play'));
      const face = document.createElement('img');
      face.className = 'menu-worm__face';
      face.src = '/images/space-worm.svg';
      face.alt = '🪱';
      const plate = document.createElement('span');
      plate.className = 'menu-worm__glow';
      btn.append(face, plate);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimWorm();
        this.scene.start('Worm', { lang, startRung });
      });

      const dom = this.add.dom(x, y, btn);
      this.worms.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 14, duration: 2100, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut', delay: 1500 + i * 240,   // offset from every other treat's bob
      });
    }
  }

  /**
   * 🚀 Rocket "treats" — one per banked Rocket Read run (earned at star 15, the violet Sun, capped
   * at ROCKET_MAX by availableRockets). specs/rocket-read-mode.md §3.2.
   *
   * Positions come from `ui/menuSlots.ts`: the inner column's second row, `(345, 315)` and
   * `(935, 315)`, the last symmetric pair on the menu. Drawn only in the languages the mode has
   * content for (ROCKET_READ_LANGS) — elsewhere nothing here appears, not even under `?treats`.
   */
  private addRocket(progression: ReadingProgression, lang: string): void {
    if (!isRocketReadLang(lang)) return;
    // Dev/staging only: `?rocket` forces a Rocket to appear and makes it replayable (it doesn't
    // spend from the bank); `?rocket=15` also starts the run at round 15, where the clock bites.
    // A finished lesson shows one free forever. All three, plus `?treats`, come back as `forced`.
    const { count, free: forced } = treatSlots({
      banked: progression.availableRockets(),
      cap: ROCKET_MAX,
      journeyDone: progression.journeyDone(),
      showAll: devFlag('treats'),
      forceOne: devFlag('rocket'),
    });
    if (count <= 0) return;

    const startRound = Number(devParam('rocket') ?? '') || 1;
    const slots = TREAT_SLOTS.rocket;

    for (let i = 0; i < count && i < slots.length; i++) {
      const { x, y } = slots[i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-rocket';
      btn.setAttribute('aria-label', i18next.t('rocket.play'));
      const ship = document.createElement('span');
      ship.className = 'menu-rocket__ship';
      ship.textContent = '🚀';
      const plate = document.createElement('span');
      plate.className = 'menu-rocket__glow';
      btn.append(ship, plate);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimRocket();
        this.scene.start('RocketRead', { lang, startRound });
      });

      const dom = this.add.dom(x, y, btn);
      this.rockets.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 14, duration: 1700, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut', delay: 700 + i * 260,   // offset from every other treat's bob
      });
    }
  }

  /**
   * 🌌 The Galaxy "treat" — one banked Word Wagon run, earned by reaching the top of the planet
   * ladder (GALAXY_AT_STACK_STARS) in a single game. specs/wagon-mode.md §3.2.
   *
   * **There is exactly one**, so there is no fill order and nothing to balance: it takes the third
   * row of the outer column at x 90, the slot `addCube` above defines and ICE_MAX never reaches.
   * That is the last free slot on this menu — 6 UFOs + 4 Diamonds + 4 Cubes + 1 Galaxy, and the
   * facing slot at x 1190 is the only thing left in the building. A sixth treat kind must replace
   * something.
   *
   * Same 96 x 76 button box and x band (42-138) as `.menu-cube`, so nothing already on the menu
   * moves by a pixel, and like every one of its siblings Phaser owns the element's transform —
   * CSS must not animate it.
   */
  private addGalaxy(progression: ReadingProgression, lang: string): void {
    // Dev/staging only: `?wagon` forces the Galaxy to appear (no stars needed) and makes it
    // replayable — it doesn't spend from the bank. `?wagon=5` also starts the run on wagon 5, where
    // the seven-lane board and the forced sacrifices live. See src/game/dev.ts.
    // ...and a finished lesson shows one of these free forever (specs §6.5). Both cases, plus
    // `?treats`, come back as `forced` — the tap launches the run without claiming.
    const { count, free: forced } = treatSlots({
      banked: progression.availableGalaxies(),
      cap: GALAXY_MAX,
      journeyDone: progression.journeyDone(),
      showAll: devFlag('treats'),
      forceOne: devFlag('wagon'),
    });
    if (count <= 0) return;

    const startRound = Number(devParam('wagon') ?? '') || 1;

    // Bottom of the outer column first, then the gap above it — mirrored by the Satellite on the
    // right, so the two treats that live at the ends of the ladder stay one per side.
    // The third takes the bottom row's inner slot on the same side, so the three read as one
    // cluster rather than as a column with something added. That row is where the saucers used to
    // sit before UFO_MAX dropped to four.
    const slots = TREAT_SLOTS.galaxy;

    for (let i = 0; i < count && i < slots.length; i++) {
      const { x, y } = slots[i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-galaxy';
      btn.setAttribute('aria-label', i18next.t('wagon.play'));
      // The icon is PlanetStack's OWN galaxy — the spiral the run pulled back to, rasterized once
      // and cached — rather than the 🌌 emoji, which renders as a square photo tile on most
      // platforms and looks like nothing the player has ever seen in this game. The emoji stays as
      // the fallback for a renderer that will not hand its pixels back (portrait() returns null).
      const art = PlanetStack.portrait(this, 'galaxy', 128);   // 2.6x the 48px it is drawn at
      let spiral: HTMLElement;
      if (art) {
        const img = document.createElement('img');
        img.src = art;
        img.alt = '';
        img.draggable = false;
        spiral = img;
      } else {
        spiral = document.createElement('span');
        spiral.textContent = '🌌';
      }
      spiral.className = 'menu-galaxy__spiral';
      const plate = document.createElement('span');
      plate.className = 'menu-galaxy__glow';
      btn.append(spiral, plate);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimGalaxy();
        this.scene.start('Wagon', { lang, startRound });
      });

      const dom = this.add.dom(x, y, btn);
      this.galaxies.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 13, duration: 2100, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut', delay: 600 + i * 190,   // offset from the saucers, the gems AND the cubes
      });
    }
  }

  /**
   * 🛰️ Satellite "treat" — spent on a Word Labyrinth run (specs/labyrinth-mode.md §3.2).
   *
   * The right of the outer column, at (1190, 480) and (1190, 398), facing the Wagon's Galaxies
   * across the bottom of the menu. The split down the middle is wagon-mode.md §3.2's ruling; what
   * has changed since is `SAT_MAX`, from 1 to 2, because y 398 turned out to be free on both sides
   * and the cap of one had only ever been a shortage (specs/labyrinth-mode.md §3.2).
   *
   * Filled bottom-up, so a single Satellite sits where a single one always did.
   */
  private addSatellite(progression: ReadingProgression, lang: string): void {
    // Dev/staging only: `?labyrinth` forces the Satellite to appear (no stars needed) and makes it
    // replayable. `?labyrinth=3` also starts the run on round 3, where the chained sentences begin.
    // ...and a finished lesson shows one of these free forever (specs §6.5). Both cases, plus
    // `?treats`, come back as `forced` — the tap launches the run without claiming.
    const { count, free: forced } = treatSlots({
      banked: progression.availableSatellites(),
      cap: SAT_MAX,
      journeyDone: progression.journeyDone(),
      showAll: devFlag('treats'),
      forceOne: devFlag('labyrinth'),
    });
    if (count <= 0) return;

    const startRound = Number(devParam('labyrinth') ?? '') || 1;

    // Bottom of the outer column first, then the gap above it — the Galaxy mirrors this on the
    // left, so the two treats that live at the ends of the ladder stay one per side however many
    // of each are banked.
    // Mirrors the Galaxy's three across the menu, which is the arrangement wagon-mode.md §3.2
    // argued for when there was one of each: the two ends of the ladder facing each other.
    const slots = TREAT_SLOTS.satellite;

    for (let i = 0; i < count && i < slots.length; i++) {
      const { x, y } = slots[i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-sat';
      btn.setAttribute('aria-label', i18next.t('labyrinth.play', { defaultValue: 'Word labyrinth' }));
      const sat = document.createElement('span');
      sat.className = 'menu-sat__glyph';
      sat.textContent = '🛰️';
      const plate = document.createElement('span');
      plate.className = 'menu-sat__glow';
      btn.append(sat, plate);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimSatellite();
        this.scene.start('Labyrinth', { lang, startRound });
      });

      const dom = this.add.dom(x, y, btn);
      this.satellites.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 13, duration: 2300, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut', delay: 900 + i * 190,   // offset from the saucers, gems, cubes, plates and galaxies
      });
    }
  }

  /**
   * 🍝 Plate "treats" — one per banked Recipe Kitchen run (earned at the eclipse, star 14, capped
   * at PASTA_MAX by availablePlates). specs/recipe-mode.md §3.2.
   *
   * They sit in the outer column's **upper gap**, at (90, 233) and (1190, 233) — the same 96x76 box
   * and the same y the Diamonds have used since they were built, one column further out. That the
   * slot exists at all corrects this menu's long-standing "the outer column is full": the cubes
   * occupy y 112-188 / 277-353 / 442-518 at x 90, so the 89px bands between them are free exactly
   * as they are at x 345, and `addDiamond` proves a button of this size fits one. The pair at
   * y 398 is still free after this; the menu's real ceiling is 20 buttons.
   *
   * Filled L then R so a single plate does not look lost on one side.
   */
  private addPlate(progression: ReadingProgression, lang: string): void {
    // Dev/staging only: `?recipe` forces a Plate to appear (no stars needed) and makes it
    // replayable — it doesn't spend from the bank. `?recipe=4` also starts the run on round 4,
    // where the card starts folding and the shelf is at its widest. See src/game/dev.ts.
    // ...and a finished lesson shows one of these free forever (specs §6.5). Both cases, plus
    // `?treats`, come back as `forced` — the tap launches the run without claiming.
    const { count, free: forced } = treatSlots({
      banked: progression.availablePlates(),
      cap: PASTA_MAX,
      journeyDone: progression.journeyDone(),
      showAll: devFlag('treats'),
      forceOne: devFlag('recipe'),
    });
    if (count <= 0) return;

    const startRound = Number(devParam('recipe') ?? '') || 1;
    // Two in the outer column's upper gaps, and the third on the bottom row at the diamond
    // column's x — the only free slot left that is not inside another treat's cluster. It is the
    // one tile on this menu that does not sit with its own kind; (935, 480) beside it is the last
    // free position on the board, so a fourth plate or a seventh treat kind has somewhere to go.
    const slots = TREAT_SLOTS.plate;

    for (let i = 0; i < count && i < slots.length; i++) {
      const { x, y } = slots[i];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-plate';
      btn.setAttribute('aria-label', i18next.t('recipe.play', { defaultValue: 'Recipe Kitchen' }));
      const glyph = document.createElement('span');
      glyph.className = 'menu-plate__glyph';
      glyph.textContent = '🍝';
      const glow = document.createElement('span');
      glow.className = 'menu-plate__glow';
      btn.append(glyph, glow);
      btn.addEventListener('click', () => {
        if (!forced) progression.claimPlate();
        this.scene.start('Recipe', { lang, startRound });
      });

      const dom = this.add.dom(x, y, btn);
      this.plates.push(dom);
      this.tweens.add({
        targets: dom, y: dom.y - 11, duration: 1900, yoyo: true, repeat: -1,
        // Its own phase, like every other treat: five kinds bobbing in step reads as one widget.
        ease: 'Sine.easeInOut', delay: 600 + i * 220,
      });
    }
  }

  /**
   * In-canvas confirmation for the destructive reset (we avoid window.confirm — browser
   * modals freeze the WKWebView/TWA event loop). Calls onConfirm only on explicit confirm.
   */
  private showResetConfirm(onConfirm: () => void): void {
    const overlay = this.add.container(0, 0).setDepth(1000);

    // The settings buttons live in the DOM layer, which sits ABOVE the canvas — hide it so it
    // can't float over this (canvas) modal. Restored when the dialog is dismissed.
    const domLayer = this.game.domContainer;
    if (domLayer) domLayer.style.display = 'none';
    const dismiss = (): void => {
      if (domLayer) domLayer.style.display = '';
      overlay.destroy();
    };

    const scrim = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setInteractive();   // swallow clicks behind the dialog
    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 720, 320, 0x1a1f25)
      .setStrokeStyle(2, 0x3a3f47);

    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 100, i18next.t('reset.title'), {
      fontFamily: FONT_STACK, fontSize: '34px', color: '#ff6b6b',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, i18next.t('reset.body'), {
      fontFamily: FONT_STACK, fontSize: '22px', color: '#dfe6ee', align: 'center',
      wordWrap: { width: 640 },
    }).setOrigin(0.5);

    const cancel = this.add.text(GAME_WIDTH / 2 - 140, GAME_HEIGHT / 2 + 90, i18next.t('reset.cancel'), {
      fontFamily: FONT_STACK, fontSize: '26px', color: '#7ec4ff',
      backgroundColor: '#243240', padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    cancel.on('pointerdown', dismiss);

    const confirm = this.add.text(GAME_WIDTH / 2 + 140, GAME_HEIGHT / 2 + 90, i18next.t('reset.confirm'), {
      fontFamily: FONT_STACK, fontSize: '26px', color: '#ffffff',
      backgroundColor: '#a02020', padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    confirm.on('pointerdown', () => {
      dismiss();
      this.showResetChallenge(onConfirm);
    });

    overlay.add([scrim, panel, title, body, cancel, confirm]);
  }

  /**
   * Step two of the reset: a small multiplication, four answers, one tap.
   *
   * Not a nag — a lock with a key only a grown-up (or a much older child) has. Everything else on
   * this screen is reachable by a five-year-old on purpose, and this is the one control that
   * destroys weeks of their work, so the gate has to be something the person it protects against
   * cannot pass. A second "are you sure?" is not that: it is one more tap, in the same place, and a
   * child who tapped the first will tap the second.
   *
   * A WRONG answer dismisses without resetting rather than offering another go. That is the safe
   * direction to fail in, and the bar is not visibly moved, which is the answer to "did something
   * just happen?".
   */
  private showResetChallenge(onConfirm: () => void): void {
    const a = Phaser.Math.Between(3, 9);
    const b = Phaser.Math.Between(3, 9);
    const answer = a * b;
    // Three near misses, so the four buttons cannot be told apart by size — guessing is 1-in-4,
    // and the numbers are re-drawn every time, so the gate cannot be learned by repetition.
    const options = new Set<number>([answer]);
    while (options.size < 4) {
      const delta = Phaser.Math.Between(1, 9) * (Math.random() < 0.5 ? -1 : 1);
      if (answer + delta > 0) options.add(answer + delta);
    }
    const shuffled = Phaser.Utils.Array.Shuffle([...options]);

    const overlay = this.add.container(0, 0).setDepth(1000);
    const domLayer = this.game.domContainer;
    if (domLayer) domLayer.style.display = 'none';
    const dismiss = (): void => {
      if (domLayer) domLayer.style.display = '';
      overlay.destroy();
    };

    const scrim = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setInteractive();
    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 720, 320, 0x1a1f25)
      .setStrokeStyle(2, 0x3a3f47);

    const question = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 90,
      i18next.t('reset.challenge', { a, b }), {
        fontFamily: FONT_STACK, fontSize: '30px', color: '#dfe6ee', align: 'center',
        wordWrap: { width: 640 },
      }).setOrigin(0.5);

    const buttons: Phaser.GameObjects.Text[] = shuffled.map((value, i) => {
      const btn = this.add.text(GAME_WIDTH / 2 + (i - 1.5) * 150, GAME_HEIGHT / 2 + 10, String(value), {
        fontFamily: FONT_STACK, fontSize: '34px', color: '#ffffff',
        backgroundColor: '#243240', padding: { x: 26, y: 14 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        if (value !== answer) {
          // Wrong: mark it for a moment (a silent close reads as a bug), then leave everything
          // alone. The timed close is the nicety; the guarantees are that the answers are dead from
          // here and that ANY tap now closes the dialog — a wrong answer must never be able to
          // strand someone in a panel whose only live control is one small button.
          buttons.forEach((o) => o.disableInteractive());
          btn.setBackgroundColor('#a02020');
          scrim.once('pointerdown', dismiss);
          this.time.delayedCall(450, dismiss);
          return;
        }
        onConfirm();
        dismiss();
      });
      return btn;
    });

    const cancel = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 110, i18next.t('reset.cancel'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#7ec4ff',
      backgroundColor: '#243240', padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    cancel.on('pointerdown', dismiss);

    overlay.add([scrim, panel, question, ...buttons, cancel]);
  }
}

